import React, { useState, useEffect } from "react";
import "../Styles/BlackProfileView.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiUser } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaRegFileAlt } from "react-icons/fa";
import ApiService from "../nhrc_apis/ApiService";

const AdminBlackProfileView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [data, setData] = useState(location.state?.employee || null);
  const [loading, setLoading] = useState(false);

  // ============================
  // FALLBACK: fetch by id only if no data was passed via state
  // (e.g. page was refreshed or opened directly with an id in the URL)
  // ============================
  useEffect(() => {
    if (!data && id) {
      fetchById();
    }
  }, [id, data]);

  const fetchById = async () => {
    try {
      setLoading(true);
      const response =
        await ApiService.BlackProfilesAPI.getBlackProfileById(id);
      console.log("GET BY ID RESPONSE:", response.data);
      setData(response.data);
    } catch (error) {
      console.error("GET BY ID ERROR:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate(-1);

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt || "");
    alert("Copied: " + txt);
  };

  // ============================
  // LOADING STATE
  // ============================
  if (loading) {
    return (
      <p style={{ color: "#fff", textAlign: "center", marginTop: 30 }}>
        Loading employee data...
      </p>
    );
  }

  if (!data) {
    return (
      <p style={{ color: "#fff", textAlign: "center", marginTop: 30 }}>
        No employee data found.
      </p>
    );
  }

  return (
    <div className="nhrc-bpv-page">
      <div className="nhrc-bpv-card">

        {/* 🔹 HR Top Bar */}
        <div className="nhrc-bpv-topbar">
          <p className="nhrc-bpv-hrname">
            <FiUser className="nhrc-bpv-hr-icon" />
            HR – {data.hr_name || "Unknown"}
          </p>

          <div className="nhrc-bpv-topbar-actions">
            <button className="nhrc-bpv-back-btn" onClick={handleBack}>
              Back
            </button>
            <BsThreeDotsVertical className="nhrc-bpv-icon-dot" />
          </div>
        </div>

        {/* 🔹 Inner UI */}
        <div className="nhrc-bpv-inner">
          <p className="nhrc-bpv-main-title">
            Employee Black Profile
          </p>

          {/* 🔹 Profile */}
          <div className="nhrc-bpv-profile-row">
            <div className="nhrc-bpv-avatar-wrap">
              <img
                src={data.photo_url || ""}
                alt="dp"
                className="nhrc-bpv-avatar"
              />
            </div>

            <div className="nhrc-bpv-profile-info">
              <span className="nhrc-bpv-name">
                {data.employee_name}
              </span>

              <span className="nhrc-bpv-title">
                {data.designation}
              </span>

              <span
                className="nhrc-bpv-status-pill"
                style={{
                  background:
                    data.status === "Active" ? "#0f8a33" : "#ff4444",
                }}
              >
                {data.status}
              </span>
            </div>
          </div>

          {/* 🔹 Identifiers */}
          <div className="nhrc-bpv-section pb-3">
            <p className="nhrc-bpv-section-title">Identifiers</p>

            <div className="nhrc-bpv-grid two-cols">
              <div>
                <p className="nhrc-bpv-label">UAN Number</p>
                <p className="nhrc-bpv-value">{data.uan_number}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">Employee ID</p>
                <p className="nhrc-bpv-value">{data.employee_id}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">Aadhaar</p>
                <p className="nhrc-bpv-value">{data.aadhaar_number}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">PAN Card</p>
                <p className="nhrc-bpv-value">{data.pan_number}</p>
              </div>
            </div>

            <div className="nhrc-bpv-button-row">
              <button
                className="nhrc-bpv-primary-btn"
                onClick={() => copyToClipboard(data.uan_number)}
              >
                Copy UAN
              </button>

              <button className="nhrc-bpv-secondary-btn">
                Hide Aadhaar
              </button>
            </div>

            <p className="nhrc-bpv-warning">
              Only authorized admins should view full Aadhaar.
            </p>
          </div>

          <br />

          {/* 🔹 Contact */}
          <div className="nhrc-bpv-section">
            <p className="nhrc-bpv-section-title">Contact</p>

            <div className="nhrc-bpv-grid two-cols">
              <div>
                <p className="nhrc-bpv-label">Email</p>
                <p className="nhrc-bpv-value">{data.email}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">Phone</p>
                <p className="nhrc-bpv-value">{data.phone}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">Location</p>
                <p className="nhrc-bpv-value">{data.location}</p>
              </div>
            </div>
          </div>

          <br />

          {/* 🔹 Employment */}
          <div className="nhrc-bpv-section">
            <p className="nhrc-bpv-section-title">Employment</p>

            <div className="nhrc-bpv-grid four-cols">
              <div>
                <p className="nhrc-bpv-label">Department</p>
                <p className="nhrc-bpv-value">{data.department}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">Experience</p>
                <p className="nhrc-bpv-value">{data.experience}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">Joining Date</p>
                <p className="nhrc-bpv-value">{data.date_of_joining}</p>
              </div>

              <div>
                <p className="nhrc-bpv-label">Reporting To</p>
                <p className="nhrc-bpv-value">{data.reporting_to}</p>
              </div>
            </div>
          </div>

          <br />

          {/* 🔹 Remarks */}
          <div className="nhrc-bpv-section">
            <p className="nhrc-bpv-section-title">Remarks</p>
            <ul className="nhrc-bpv-remarks-list">
              <li>{data.remarks || "No remarks available"}</li>
            </ul>
          </div>

          <br />

          {/* 🔹 Documents */}
          <div className="nhrc-bpv-section">
            <p className="nhrc-bpv-section-title">Documents</p>

            <div className="nhrc-bpv-docs-grid">
              {data.document_url ? (
                <div className="nhrc-bpv-doc-card">
                  <FaRegFileAlt className="nhrc-bpv-doc-icon" />
                  <span className="nhrc-bpv-doc-title">
                    {data.document_name || "Document"}
                  </span>
                </div>
              ) : (
                <p style={{ color: "#bbb" }}>No documents uploaded</p>
              )}
            </div>

            <p className="nhrc-bpv-doc-note">
              Documents are blurred by default. Request to view securely.
            </p>
          </div>

          <br />

          <div
            className="nhrc-bpv-section"
            style={{ background: "#2a0000" }}
          >
            <p
              style={{
                fontSize: 12,
                color: "#ffb4b4",
                fontWeight: 500,
                marginTop: "10px",
              }}
            >
              This profile contains Personally Identifiable Information (PII). Do not share or record. Access is logged.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminBlackProfileView;